import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import { PersistUserRequest, VerifyEmailRequest, SimpleUserModel } from "../../models/manage/user.models";
import { RightshipRuleConfigParameterModel, PersistRightshipRuleConfigParametersRequest, MoveUserToCustomerRequest, MoveUserToCustomerResponse, BulkChangeUserRoleRequest } from "../../models/customers/customer.models";
import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { UserClientService } from "../authentication/user-client.service";
import { CustomerSubscriptionService } from '../../services/modules/customer-subscription-client.service';
import { SubscriptionTypeEnum } from "../../models/customers/customer-subscription.models";

@autoinject()
export class ManageRightShipUsersClientService {

    private _getRightshipRuleConfigParametersUnitCache: RightshipRuleConfigParameterModel[];
    private _getRightshipRuleConfigParametersPromiseCache: Promise<RightshipRuleConfigParameterModel[]>;

    constructor(private requestClient: RequestClientService, private cacheClient: CacheClientService,
        private userClientService: UserClientService, private customerSubscriptionService: CustomerSubscriptionService) { }

    delete(userIds: number[]) {
        this.requestClient.post<any>("rightShipQiUser/delete", {}, { userIds: userIds });
    }

    getMe(): Promise<any> {

        return new Promise((resolve, reject) => {
            this.requestClient.get<any>("rightShipQiUser/GetMe", {}).then(x => {
                this.cacheClient.put("rightshipqi_user_" + x.user.userId, x);
                resolve(x);
            }, err => {
                reject(err);
            }).catch(err => {
                reject(err);
            });
        });

    }

    public updateUserMfaSettings() {
        return this.requestClient.post("rightShipQiUser/UpdateMfaSettings");
    }

    resetUserMfaEnrollment(userId?: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestClient.post<void>("rightShipQiUser/resetUserMfaEnrollment", { userid: userId }).then(() => {
                resolve();
            },  () => {
                reject();
            }
            )
        });
    }

    getUser(userId: number): Promise<any> {

        return new Promise((resolve, reject) => {
            this.requestClient.get<any>("rightShipQiUser/" + userId, {}).then(x => {
                const cacheValue = x.businessUnit ? x : {};
                this.cacheClient.put("rightshipqi_user_" + x.user.userId, cacheValue);
                resolve(x);
            }, err => {
                reject(err);
            }).catch(err => {
                reject(err);
            });
        });

    }

    getUsersFromBusinessUnits(businessUnitIds: number[]): Promise<any> {
        return this.requestClient.post<any>("rightShipQiUser/getUsersFromBusinessUnits", {}, { BusinessUnitsIds: businessUnitIds });
    }

    getBusinessUnitUser(userId: number) {
        return this.cacheClient.get<any>("rightshipqi_user_" + userId);
    }

    save(request: PersistUserRequest): Promise<any> {
        return this.requestClient.post<any>("rightShipQiUser/save", {}, request);
    }

    bulkChangeRole(request: BulkChangeUserRoleRequest): Promise<any> {
        return this.requestClient.post<any>("rightShipQiUser/bulkChangeRole", {}, request);
    }

    verifyEmail(request: VerifyEmailRequest): Promise<any> {
        return this.requestClient.post<any>("rightShipQiUser/verifyEmail", {}, request);
    }

    updateUserActivity(request: { ids: number[], activate?: boolean }) {
        this.requestClient.post<any>("rightShipQiUser/updateUserActivity", {}, request);
    }

    getUserPrivacyPolicyAgreement(): Promise<any> {
        return this.requestClient.get<any>("rightShipQiUser/getUserPrivacyPolicyAgreement", {})
    }

    acceptUserPrivacyPolicyAgreement(): Promise<any> {
        return this.requestClient.get<any>("rightShipQiUser/acceptUserPrivacyPolicyAgreement", {})
    }

    resetDirectPassword(newPassword: string): Promise<any> {
        return this.requestClient.post<any>("rightShipQiUser/resetDirectPassword", {}, { newPassword: newPassword });
    }

    getSimplifiedUsersFromBusinessUnit(businessUnitId: number): Promise<SimpleUserModel[]> {
        return this.requestClient.get<any>("rightShipQiUser/getSimplifiedUsersFromBusinessUnit", { businessUnitId });
    }

    async getRsRuleConfigParameters() {
        if (this._getRightshipRuleConfigParametersUnitCache)
            return this._getRightshipRuleConfigParametersUnitCache;

        if (this._getRightshipRuleConfigParametersPromiseCache)
            return await this._getRightshipRuleConfigParametersPromiseCache;

        this._getRightshipRuleConfigParametersPromiseCache = this.requestClient.get<RightshipRuleConfigParameterModel[]>("rightShipQiUser/getRightshipRuleConfigParameters", {});

        this._getRightshipRuleConfigParametersUnitCache = await this._getRightshipRuleConfigParametersPromiseCache;
        return this._getRightshipRuleConfigParametersUnitCache;
    }

    saveRuleConfigParameters(request: PersistRightshipRuleConfigParametersRequest): Promise<RightshipRuleConfigParameterModel> {
        return this.requestClient.post<RightshipRuleConfigParameterModel>("rightShipQiUser/saveRuleConfigParameters", {}, request);
    }

    moveUserToNewCustomer(request: MoveUserToCustomerRequest) {
        return this.requestClient.post<MoveUserToCustomerResponse>("rightShipQiUser/move", {}, request);
    }

    getBranchRoles(branchId: number) {
        return this.requestClient.post<any[]>("rightShipQiUser/getBranchRoles", {}, branchId);
    }

    getOfficeLocationOfUser(userId: number): Promise<any> {
        return this.requestClient.get<number>("rightShipQiUser/GetOfficeLocationOfUser", { userId });
    }

    async isLimitedCustomerSubscription(productCode: string, shouldCheckForVessels: boolean = false) {
        if (this.userClientService.currentUser) {
            const response = await this.customerSubscriptionService.getMySubscription(productCode);
            return (response?.customerSubscription?.subscriptionTypeId === SubscriptionTypeEnum.Limited || response?.customerSubscription?.isExpired) && 
                (!shouldCheckForVessels || !response?.customerSubscription?.maxVesselsCount);
        }

        return false;
    }
}
